import styles from './AviosCurrencyBadge.module.css';
import AviosCurrency, {
  AviosCurrencyProps,
} from '../AviosCurrency/AviosCurrency';
import { cva } from 'class-variance-authority';
import { useMemo, type ReactNode, isValidElement } from 'react';

const aviosCurrencyBadge = cva(styles['avios-currency-badge'], {
  variants: {
    size: {
      xs: styles['avios-currency-badge--xs'],
      sm: styles['avios-currency-badge--sm'],
      md: styles['avios-currency-badge--md'],
      lg: styles['avios-currency-badge--lg'],
      xl: styles['avios-currency-badge--xl'],
    },
    kind: {
      collect: styles['avios-currency-badge--collect'],
      spend: styles['avios-currency-badge--spend'],
    },
  },
  defaultVariants: {
    size: 'md',
    kind: 'collect',
  },
});

export const textCva = cva(styles['avios-currency-badge__text'], {
  variants: {
    // we pull in size so we can fire off the .xs-bold/.sm-bold/etc macros
    size: {
      xs: undefined,
      sm: undefined,
      md: undefined,
      lg: undefined,
      xl: undefined,
    },
    bold: {
      true: undefined,
      false: undefined,
    },
  },
  defaultVariants: {
    size: 'md',
    bold: false,
  },
  compoundVariants: [
    {
      size: 'xs',
      bold: true,
      className: styles['avios-currency-badge__text--xs-bold'],
    },
    {
      size: 'sm',
      bold: true,
      className: styles['avios-currency-badge__text--sm-bold'],
    },
    {
      size: 'md',
      bold: true,
      className: styles['avios-currency-badge__text--md-bold'],
    },
    {
      size: 'lg',
      bold: true,
      className: styles['avios-currency-badge__text--lg-bold'],
    },
    {
      size: 'xl',
      bold: true,
      className: styles['avios-currency-badge__text--xl-bold'],
    },
  ],
});

export const BADGE_SIZES = ['xs', 'sm', 'md', 'lg', 'xl'] as const;
export type BadgeSize = (typeof BADGE_SIZES)[number];

export interface AviosCurrencyBadgeProps
  extends Omit<
    AviosCurrencyProps,
    'isStrikethrough' | 'decorator' | 'styleVariant'
  > {
  /**
   * The kind of Avios badge (collect or spend)
   * @default 'collect'
   */
  kind?: 'collect' | 'spend';
  /**
   * Additional content to be displayed at the start of the badge
   */
  textStart?: ReactNode;
  /**
   * Additional content to be displayed at the end of the badge
   */
  textEnd?: ReactNode;
  /**
   * Whether the text at the start of the badge should be bold
   * @default false
   */
  textStartBold?: boolean;
  /**
   * Whether the text at the end of the badge should be bold
   * @default false
   */
  textEndBold?: boolean;
  /**
   * Size of the badge
   * @default 'md'
   */
  size?: BadgeSize;
  /**
   * A string representation of the badge's contents, used for accessibility
   * Example: "Collect 50,000 Avios points"
   */
  ariaLabel?: string;
  /**
   * ARIA role for the badge container. This should only be used when changes to the currency badge need to be announced.
   */
  role?: string;
  /**
   * Number of Avios points
   */
  children?: number;
}

const sizeMap: Record<BadgeSize, AviosCurrencyProps['size']> = {
  xs: '5xs',
  sm: '4xs',
  md: '3xs',
  lg: '2xs',
  xl: 'xs',
};

function extractText(node: ReactNode): string {
  if (typeof node === 'string' || typeof node === 'number') return String(node);
  if (Array.isArray(node)) return node.map(extractText).join(' ');
  if (isValidElement(node) && node.props && node.props.children) {
    return extractText(node.props.children);
  }
  return '';
}

export const AviosCurrencyBadge = ({
  kind = 'collect',
  size = 'md',
  textStart,
  textEnd,
  textStartBold = false,
  textEndBold = false,
  ariaLabel,
  role,
  children = 0,
  ...props
}: AviosCurrencyBadgeProps) => {
  const numericChildren = Number(children);

  const formattedPoints = new Intl.NumberFormat('en-GB', {
    style: 'decimal',
    maximumFractionDigits: 0,
  }).format(numericChildren);

  const computedAriaLabel = useMemo(() => {
    if (ariaLabel) return ariaLabel;

    const formatted = formattedPoints ?? '';
    const startText = extractText(textStart);
    const endText = extractText(textEnd);
    return `${startText || ''} ${formatted} Avios ${endText || ''}`.trim();
  }, [ariaLabel, textStart, textEnd, formattedPoints]);

  const aviosCurrencySize = sizeMap[size] ?? '3xs';

  return (
    <span
      data-testid="avios-currency-badge"
      aria-label={computedAriaLabel}
      role={role || undefined}
      className={aviosCurrencyBadge({
        size,
        kind,
      })}
    >
      {textStart && (
        <span className={textCva({ size, bold: textStartBold })}>
          {textStart}
        </span>
      )}
      <AviosCurrency
        aria-hidden
        styleVariant={kind === 'collect' ? 'onCollect' : 'onSpend'}
        size={aviosCurrencySize}
        kind={kind}
        {...props}
      >
        {Number.isFinite(numericChildren) ? numericChildren : 0}
      </AviosCurrency>
      {textEnd && (
        <span className={textCva({ size, bold: textEndBold })}>{textEnd}</span>
      )}
    </span>
  );
};

export default AviosCurrencyBadge;
